// Conversão — funil aquisição 30d, taxa de pagamento por semana, status pedidos mês.
import { useEffect, useState } from "react";
import { api } from "../../api.ts";
import { FunnelBars, BarChart, DonutChart } from "../../components/charts/index.ts";
import { fmtBRL, fmtNum } from "../../lib/format.ts";

interface Payload {
  funnel_30d: {
    sessions: number;
    add_to_cart: number;
    checkout: number;
    orders: number;
    paid: number;
  };
  payment_rate_12w: Array<{ week: string; orders: number; paid: number; cancelled: number; payment_rate: number }>;
  status_breakdown_mtd: Array<{ status: string; count: number; revenue: number }>;
}

const STATUS_COLOR: Record<string, string> = {
  pago: "#047857",
  enviado: "#0891b2",
  "aguardando pagto": "#b45309",
  cancelado: "#b91c1c",
};

function weekLabel(iso: string) {
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function ConversaoPanel({ slug }: { slug: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Payload>(`/api/t/${slug}/tactical/conversao`).then(setData).catch((e) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="card"><p style={{ color: "var(--red)" }}>erro: {err}</p></div>;
  if (!data) return <div className="center-loading">carregando…</div>;

  const f = data.funnel_30d;
  const stages = [
    { label: "Sessões", value: f.sessions },
    { label: "Add to cart", value: f.add_to_cart },
    { label: "Checkout iniciado", value: f.checkout },
    { label: "Pedidos criados", value: f.orders },
    { label: "Pedidos pagos", value: f.paid },
  ].filter((s) => s.value > 0);

  // Se Matomo não tá mandando add_to_cart/checkout, mostra mínimo (sessões → pedidos → pagos)
  const hasMatomoEvents = f.add_to_cart > 0 || f.checkout > 0;
  const stagesShown = hasMatomoEvents
    ? stages
    : [
      { label: "Sessões 30d (Matomo)", value: f.sessions },
      { label: "Pedidos criados", value: f.orders },
      { label: "Pedidos pagos", value: f.paid },
    ].filter((s) => s.value > 0);

  const payRateChart = data.payment_rate_12w
    .filter((w) => w.orders > 0)
    .map((w) => ({ label: weekLabel(w.week), value: w.payment_rate }));

  const statusSlices = data.status_breakdown_mtd
    .filter((s) => s.count > 0)
    .map((s) => ({ name: s.status, value: s.count, color: STATUS_COLOR[s.status] }));

  const overallConv = f.sessions > 0 ? (f.paid / f.sessions) * 100 : 0;
  const totalMtd = data.status_breakdown_mtd.reduce((s, x) => s + x.count, 0);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="label">Sessões · 30d</div>
          <div className="value">{fmtNum(f.sessions)}</div>
          <div className="meta"><span className="mono">Matomo</span></div>
        </div>
        <div className="kpi-card">
          <div className="label">Pedidos pagos · 30d</div>
          <div className="value">{fmtNum(f.paid)}</div>
          <div className="meta"><span className="mono">{fmtNum(f.orders)} criados</span></div>
        </div>
        <div className="kpi-card">
          <div className="label">Conversão geral</div>
          <div className="value">{overallConv.toFixed(2)}<small>%</small></div>
          <div className="meta"><span className="mono">pagos / sessões</span></div>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <p className="eyebrow">Últimos 30 dias</p>
            <h2>Funil de aquisição</h2>
          </div>
        </div>
        <div className="card">
          <FunnelBars stages={stagesShown} formatValue={fmtNum} />
          {!hasMatomoEvents && (
            <p style={{ fontSize: ".78rem", color: "var(--text-muted)", marginTop: "1rem" }}>
              Etapas <code>add_to_cart</code>/<code>begin_checkout</code> não chegam do Matomo —
              configure os triggers na iSET pra ver o funil completo.
            </p>
          )}
        </div>
      </div>

      {payRateChart.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">12 semanas</p>
              <h2>Taxa de pagamento (% pagos / criados)</h2>
            </div>
          </div>
          <div className="card">
            <BarChart
              data={payRateChart}
              height={240}
              formatValue={(v) => `${v.toFixed(0)}%`}
            />
          </div>
        </div>
      )}

      {statusSlices.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Mês corrente</p>
              <h2>Status dos pedidos</h2>
            </div>
          </div>
          <div className="grid g2">
            <div className="card">
              <DonutChart
                data={statusSlices}
                centerLabel={{ value: fmtNum(totalMtd), sub: "pedidos no mês" }}
                height={300}
              />
            </div>
            <div className="card">
              <table className="simple">
                <thead><tr><th>Status</th><th style={{ textAlign: "right" }}>Pedidos</th><th style={{ textAlign: "right" }}>Receita</th></tr></thead>
                <tbody>
                  {data.status_breakdown_mtd.map((s, i) => (
                    <tr key={i}>
                      <td>
                        <span style={{
                          display: "inline-block", width: 10, height: 10, borderRadius: 2,
                          background: STATUS_COLOR[s.status] ?? "var(--text-muted)",
                          marginRight: ".5rem", verticalAlign: "middle",
                        }} />
                        <strong>{s.status}</strong>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">{fmtNum(s.count)}</td>
                      <td style={{ textAlign: "right" }} className="mono">{fmtBRL(s.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
